import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { JwtService } from './jwt.service';

@Injectable({
  providedIn: 'root'
})
export class AgentService {

  private isAgentSubject = new BehaviorSubject<boolean>(false);
  public isAgent$ = this.isAgentSubject.asObservable();

  private isAdminSubject = new BehaviorSubject<boolean>(false);
  public isAdmin$ = this.isAdminSubject.asObservable();

  constructor(private jwtService: JwtService) {
    this.initializeFromToken();
  }

  /**
   * Subscribe to token changes and derive role from JWT.
   * The token's `role` field is the single source of truth.
   */
  private initializeFromToken(): void {
    this.jwtService.token$.subscribe(decodedToken => {
      const role = decodedToken?.role;
      const isAdmin = role === 'admin' || role === 'platform';
      const isAgent = !isAdmin;

      this.isAdminSubject.next(isAdmin);
      this.isAgentSubject.next(isAgent);
    });
  }

  public isAgent(): Observable<boolean> {
    return this.isAgent$;
  }

  public isAgentValue(): boolean {
    return this.isAgentSubject.value;
  }

  public isAdmin(): Observable<boolean> {
    return this.isAdmin$;
  }

  public isAdminValue(): boolean {
    return this.isAdminSubject.value;
  }

  public verifyAgentAccess(): Observable<{
    isAgent: boolean;
    hasAgentParam: boolean;
    reason?: string;
  }> {
    const decodedToken = this.jwtService.decodeToken();
    const role = decodedToken?.role;
    const isAdmin = role === 'admin' || role === 'platform';
    const isAgent = !isAdmin;

    let reason: string | undefined;
    if (!decodedToken) {
      reason = 'No valid token';
    } else if (isAdmin) {
      reason = 'User role is admin/platform';
    }

    return new Observable(observer => {
      observer.next({ isAgent, hasAgentParam: isAgent, reason });
      observer.complete();
    });
  }

  public getAgentInfo(): Observable<{
    isAgent: boolean;
    email?: string;
    firstname?: string;
    surname?: string;
  }> {
    const decodedToken = this.jwtService.decodeToken();
    const role = decodedToken?.role;
    const isAgent = role !== 'admin' && role !== 'platform';

    if (isAgent && decodedToken) {
      const nameParts = (decodedToken.name || '').split(' ');
      const firstname = nameParts[0] || '';
      const surname = nameParts.slice(1).join(' ') || '';

      return new Observable(observer => {
        observer.next({
          isAgent: true,
          email: decodedToken.sub,
          firstname,
          surname
        });
        observer.complete();
      });
    }

    return new Observable(observer => {
      observer.next({ isAgent: false });
      observer.complete();
    });
  }
}
